import { cx } from "@emotion/css";
import { Ban, CircleCheck, CircleX, SkipForward } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { DeckStatRow } from "src/components/Modals/StatisticsModals/deckStatsContent";
import {
	type ExamAnswersStats,
	type ExamStats,
	getExamChartSegments,
} from "src/components/Modals/StatisticsModals/examStatsTypes";
import { DeckCircleChart } from "src/components/Statistics/flashcards/DeckCircleChart";
import { useFontSize } from "src/hooks/useFontSize";

interface ExamAnswersChartProps {
	stats: ExamAnswersStats;
	centerContent?: ReactNode;
	chartFooter?: ReactNode;
	showSkipped?: boolean;
}

/** Wykres kołowy odpowiedzi + legenda z paskami (statystyki i podsumowanie egzaminu). */
export const ExamAnswersChart = ({
	stats,
	centerContent,
	chartFooter,
	showSkipped = stats.skippedQuestions > 0,
}: ExamAnswersChartProps) => {
	const { t } = useTranslation();

	const rows = [
		{
			label: t("modals.statistics.correctAnswers", "Poprawne odpowiedzi"),
			Icon: CircleCheck,
			tintClass: "tint-success",
			scoreClassName: "text-success",
			count: stats.correctAnswers,
			testId: "stat-correct-answers",
		},
		{
			label: t("modals.statistics.wrongAnswers", "Błędne odpowiedzi"),
			Icon: CircleX,
			tintClass: "tint-error",
			scoreClassName: "text-error",
			count: stats.wrongAnswers,
			testId: "stat-wrong-answers",
		},
		{
			label: t("modals.statistics.cancelledAnswers", "Anulowane pytania"),
			Icon: Ban,
			tintClass: "tint-neutral",
			scoreClassName: "text-grey",
			count: stats.cancelledQuestions,
			testId: "stat-cancelled-questions",
		},
		...(showSkipped
			? [
					{
						label: t("modals.statistics.skippedAnswers", "Pominięte pytania"),
						Icon: SkipForward,
						tintClass: "tint-info",
						scoreClassName: "text-primary",
						count: stats.skippedQuestions,
						testId: "stat-skipped-questions",
					},
				]
			: []),
	];

	return (
		<div className="chart-with-legend">
			<DeckCircleChart
				className="circle-chart--large"
				segments={getExamChartSegments(stats)}
				total={stats.totalQuestions}
				centerContent={centerContent}
			>
				{chartFooter}
			</DeckCircleChart>
			<div className="flex flex-col gap-12 flex-1 w-100">
				{rows.map(({ count, testId, ...config }) => (
					<DeckStatRow
						key={config.label}
						config={config}
						count={count}
						total={stats.totalQuestions}
						testId={testId}
					/>
				))}
			</div>
		</div>
	);
};

interface ExamInfoStatsProps {
	stats: ExamStats;
	showScore?: boolean;
}

export const ExamInfoStats = ({
	stats,
	showScore = true,
}: ExamInfoStatsProps) => {
	const { t } = useTranslation();
	const { modalHeaderFontSizeClass } = useFontSize();

	return (
		<div className="flex flex-col gap-16 mt-24 container-inline">
			{showScore && (
				<div className="flex items-center justify-between gap-16 px-16 py-12 border-radius-16 border-light-grey bg-grey overwritten-bg-to-fill2">
					<span className="size-16">
						{t("modals.statistics.currentScore", "Aktualny wynik")}
					</span>
					<span className="size-24 font-semibold text-primary">
						{stats.score}%
					</span>
				</div>
			)}
			<span className={cx(modalHeaderFontSizeClass, "font-semibold")}>
				{t("modals.statistics.answers", "Odpowiedzi")}
			</span>
			<ExamAnswersChart stats={stats} />
		</div>
	);
};
